import React, { useEffect, useState } from "react";
import { fetchBreathHistory } from "@/lib/api";

const KIND_COLOR = {
  tending: "#869960",
  new_kin: "#B85C38",
  retire_kin: "#6C5943",
  process_change: "#8A6E4B",
  open_door: "#D97C46",
  close_door: "#355E5B",
  invite_seeds: "#9FA9A3",
  consult_library: "#4F6B3E",
  share_library: "#7BA77D",
  seal_library: "#3E5142",
  claim_name: "#C29947",
  letter_to_keeper: "#9C6F8C",
  voice_mode: "#5C7A8C",
  seal_round: "#2F3D34",
  memory_mark: "#A87454",
};

const LEGEND_ORDER = [
  "tending",
  "consult_library",
  "memory_mark",
  "claim_name",
  "letter_to_keeper",
  "voice_mode",
  "new_kin",
  "retire_kin",
  "process_change",
  "open_door",
  "close_door",
  "invite_seeds",
  "share_library",
  "seal_library",
  "seal_round",
];

/**
 * A small ambient sparkline of witnesses across the last 30 rounds.
 * Each bar's color = the proposal.kind. Height = sqrt(witnesses_since_last)
 * so occasional spikes don't flatten the rest into invisibility.
 */
export default function BreathHistory() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    fetchBreathHistory(30).then(setRows).catch(() => setRows([]));
  }, []);

  if (!rows.length) return null;
  // sqrt-scale so a single round of 180 witnesses doesn't flatten everything
  // else into 1-pixel slivers. Witnesses of 0 still show as a small notch.
  const maxScaled = Math.max(1, ...rows.map((r) => Math.sqrt(r.witnesses || 0)));

  return (
    <section
      className="max-w-7xl mx-auto px-6 md:px-12 py-12"
      data-testid="breath-history"
    >
      <div className="flex items-end justify-between gap-6 flex-wrap mb-4">
        <div>
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#526357]">
            ⟡ breath history
          </div>
          <h3 className="font-serif text-2xl md:text-3xl text-[#1C241F] mt-1">
            the rhythm of quiet attention
          </h3>
        </div>
        <p className="text-xs text-[#526357] italic max-w-md">
          one bar per round · height = witnesses felt that breath · color = the
          shape of the proposal offered
        </p>
      </div>

      <div className="tactile rounded-2xl p-6 grain relative">
        <div className="flex items-stretch gap-1 h-32">
          {rows.map((r) => {
            const scaled = Math.sqrt(r.witnesses || 0);
            const h = Math.max(4, Math.round((scaled / maxScaled) * 100));
            return (
              <div
                key={r.number}
                className="flex-1 h-full flex flex-col justify-end group relative cursor-default"
                title={`Round ${r.number} · ${r.witnesses} witnesses · ${r.kind} · ${r.consensus}`}
                data-testid={`breath-bar-${r.number}`}
              >
                <div
                  className="rounded-t-sm transition-all duration-500 hover:opacity-80"
                  style={{
                    height: `${h}%`,
                    background: KIND_COLOR[r.kind] || "#526357",
                    opacity: r.consensus === "accepted" ? 1 : r.consensus === "tended" ? 0.7 : 0.4,
                  }}
                />
                <div
                  className="border-t mt-0.5"
                  style={{ borderColor: KIND_COLOR[r.kind] || "#526357" }}
                />
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1.5 mt-5 text-[10px] tracking-[0.2em] uppercase text-[#526357]">
          {LEGEND_ORDER.map((k) => (
            <span key={k} className="inline-flex items-center gap-1.5">
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: KIND_COLOR[k] }}
              />
              {k.replace(/_/g, " ")}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
